// The karambit model: "Karambit" by Astral (sketchfab.com/Astral_27, CC-BY-4.0), simplified by
// scripts/import-karambit.mjs. Its blade takes the game's finishes (projected onto the blade's flat
// side), the edge stays polished steel, the grip black, and the inserts and ring follow the finish.
//
// The result is placed in the procedural karambit's coordinates (handle along +X toward the blade,
// hook toward -Y, grip centre at the origin) so the arm rig holds it the same way.

import * as THREE from 'three';

export const KARAMBIT_URL = `${import.meta.env.BASE_URL ?? '/'}assets/models/karambit.glb`;

/** Model -> procedural knife coordinates (measured from the model's grip and ring). */
const FIT_SCALE = 1.25;
/** Middle of the grip in model coordinates (where the fist goes). */
const GRIP_CENTRE = new THREE.Vector3(0.062, 1.739, 0);
/** The grip's long axis points 53.7 degrees from model +X toward the ring; it must point to -X. */
const GRIP_TURN = ((180 - 53.7) * Math.PI) / 180;
/** The finish canvas covers this rectangle of the procedural blade's shape coordinates. */
const UV_X = [0.9, 1.9];
const UV_Y = [-0.6, 0.25];

export interface KnifeModel {
  /** The knife, in procedural-karambit coordinates. */
  object: THREE.Group;
  /** Handle inserts and ring colour (follows the finish). */
  setAccent(color: string): void;
}
export type KarambitModel = KnifeModel;

let loading: Promise<KarambitModel | null> | null = null;

/** Load once. `blade` is the finish material (its map uses the procedural blade's UV layout). */
export function loadKarambit(blade: THREE.Material): Promise<KarambitModel | null> {
  loading ??= (async () => {
    try {
      const [{ GLTFLoader }, { toCreasedNormals }] = await Promise.all([
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/utils/BufferGeometryUtils.js'),
      ]);
      const gltf = await new GLTFLoader().loadAsync(KARAMBIT_URL);
      const steel = new THREE.MeshPhongMaterial({ color: 0xdfe4ea, specular: 0xffffff, shininess: 140 });
      const grip = new THREE.MeshPhongMaterial({ color: 0x0b0c0e, specular: 0x2a2d33, shininess: 14 });
      const accent = new THREE.MeshPhongMaterial({ color: 0x1b2f7a, specular: 0x6070a0, shininess: 40 });
      const ring = new THREE.MeshPhongMaterial({ color: 0x14182a, specular: 0xa0a8c0, shininess: 90 });
      const byPart: Record<string, THREE.Material> = {
        blade,
        edge: steel,
        ringInner: steel,
        grip,
        inserts: accent,
        accent,
        ring,
      };

      const inner = new THREE.Group();
      gltf.scene.updateMatrixWorld(true);
      const meshes: THREE.Mesh[] = [];
      gltf.scene.traverse((o) => {
        if (o instanceof THREE.Mesh) meshes.push(o);
      });
      for (const m of meshes) {
        const part = m.name.replace(/_\d+$/, '');
        // Hard edges stay hard (blade bevel, grip grooves), curved surfaces stay smooth.
        const geo = toCreasedNormals(m.geometry.clone().applyMatrix4(m.matrixWorld), (35 * Math.PI) / 180);
        if (part === 'blade') projectBladeUvs(geo);
        const mesh = new THREE.Mesh(geo, byPart[part] ?? grip);
        mesh.name = part;
        mesh.frustumCulled = false;
        inner.add(mesh);
      }
      // Model -> procedural coordinates: turn the grip axis onto -X (ring end), flip over so the hook
      // faces -Y like the procedural blade, scale, and centre the grip on the origin.
      inner.rotation.set(Math.PI, 0, GRIP_TURN, 'XYZ');
      inner.scale.setScalar(FIT_SCALE);
      inner.position.copy(GRIP_CENTRE).applyEuler(inner.rotation).multiplyScalar(-FIT_SCALE);
      const object = new THREE.Group();
      object.add(inner);
      return {
        object,
        setAccent: (c: string) => {
          accent.color.set(c);
          ring.color.set(c).multiplyScalar(0.6);
        },
      };
    } catch {
      return null;
    }
  })();
  return loading;
}

/**
 * Planar UVs for the blade, matching the finish texture's layout: the blade's flat side (model XY)
 * stretched over the canvas, base at the upper left, hooked tip at the lower right. In procedural
 * coordinates x runs base->tip (= model -Y) and y up (= model X).
 */
function projectBladeUvs(geo: THREE.BufferGeometry) {
  const pos = geo.getAttribute('position');
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    const px = -pos.getY(i);
    const py = pos.getX(i);
    minX = Math.min(minX, px);
    maxX = Math.max(maxX, px);
    minY = Math.min(minY, py);
    maxY = Math.max(maxY, py);
  }
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    const px = -pos.getY(i);
    const py = pos.getX(i);
    uv[i * 2] = UV_X[0] + ((px - minX) / (maxX - minX)) * (UV_X[1] - UV_X[0]);
    uv[i * 2 + 1] = UV_Y[0] + ((py - minY) / (maxY - minY)) * (UV_Y[1] - UV_Y[0]);
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}
